import { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { WheelTimePicker } from './WheelTimePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Camera, Image as ImageIcon, X, Eraser, CheckCircle2 } from 'lucide-react';

const MAX_PHOTOS = 5;

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const isoToHHMM = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Optional proof-of-delivery capture (signed-by name, signature, photos)
 * when a driver completes a haulage stop. All fields are optional.
 */
export const CompleteStopDialog = ({ open, onOpenChange, jobId, stopId, stopLabel, initialWorkDescription, initialArrivedAt, showSignature = true, api, onCompleted }) => {
  const canvasRef = useRef(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signedByName, setSignedByName] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [finishTime, setFinishTime] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setHasSignature(false);
      setSignedByName('');
      setWorkDescription(initialWorkDescription || '');
      setStartTime(isoToHHMM(initialArrivedAt) || nowHHMM());
      setFinishTime(nowHHMM());
      setPhotos([]);
      // Clear canvas on open (next tick so canvas is mounted)
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }, 50);
    }
  }, [open]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => { drawing.current = false; };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handlePhotoCapture = async (e, source) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) { toast.error(`Maximum ${MAX_PHOTOS} photos`); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Photo must be under 15MB'); return; }
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post('/api/mobile/docket-photos/upload', {
        photo: base64,
        captured_at: new Date().toISOString(),
        source,
      });
      setPhotos((prev) => [...prev, res.data.photo]);
      toast.success('Photo added');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (libraryInputRef.current) libraryInputRef.current.value = '';
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const payload = {
        signed_by_name: signedByName.trim() || null,
        signature_url: hasSignature && canvasRef.current ? canvasRef.current.toDataURL('image/png') : null,
        photo_urls: photos.map((p) => p.url).filter(Boolean),
        work_description: workDescription.trim() || null,
        start_time: startTime || null,
        finish_time: finishTime || null,
      };
      const res = await api.post(`/api/jobs/${jobId}/stops/${stopId}/complete`, payload);
      onCompleted(res.data.stops);
      toast.success('Stop completed');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete stop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="complete-stop-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--m-text)]">Complete Stop{stopLabel ? ` — ${stopLabel}` : ''}</DialogTitle>
          <DialogDescription className="text-[var(--m-text-secondary)] text-xs">Capture optional proof of delivery, then complete the stop.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-[var(--m-text-secondary)] text-xs">Set the start and finish time for this stop. Proof of delivery is optional.</p>

          <div className="flex items-stretch gap-2">
            <div className="flex-1 min-w-0 basis-0">
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Start time</label>
              <WheelTimePicker
                value={startTime}
                onChange={setStartTime}
                minuteStep={5}
                className="w-full h-9 rounded-md bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-sm hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                testId="complete-stop-start-time"
              />
            </div>
            <div className="flex-1 min-w-0 basis-0">
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Finish time</label>
              <WheelTimePicker
                value={finishTime}
                onChange={setFinishTime}
                minuteStep={5}
                className="w-full h-9 rounded-md bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] text-sm hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                testId="complete-stop-finish-time"
              />
            </div>
          </div>

          {showSignature && (
            <div>
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Signed by (optional)</label>
              <Input
                value={signedByName}
                onChange={(e) => setSignedByName(e.target.value)}
                placeholder="Name of person who received goods"
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                data-testid="complete-stop-signed-by"
              />
            </div>
          )}

          <div>
            <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Description of works (optional)</label>
            <textarea
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              placeholder="Any work notes for this stop..."
              rows={3}
              className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md px-3 py-2 text-[var(--m-text)] text-sm focus:outline-none focus:border-[#ffd108]"
              data-testid="complete-stop-work-description"
            />
          </div>

          {showSignature && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--m-text-secondary)]">Signature (optional)</label>
                {hasSignature && (
                  <button type="button" onClick={clearSignature} className="text-xs text-[var(--m-text-secondary)] flex items-center gap-1" data-testid="complete-stop-clear-signature">
                    <Eraser className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
              <canvas
                ref={canvasRef}
                width={400}
                height={140}
                className="w-full rounded-md bg-white touch-none"
                style={{ touchAction: 'none' }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
                data-testid="complete-stop-signature-canvas"
              />
            </div>
          )}

          <div>
            <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Photos (optional)</label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" className="flex-1 border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => cameraInputRef.current?.click()} disabled={uploading} data-testid="complete-stop-camera-btn">
                <Camera className="h-4 w-4 mr-1" /> Camera
              </Button>
              <Button type="button" size="sm" variant="outline" className="flex-1 border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => libraryInputRef.current?.click()} disabled={uploading} data-testid="complete-stop-library-btn">
                <ImageIcon className="h-4 w-4 mr-1" /> Library
              </Button>
            </div>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoCapture(e, 'camera')} />
            <input ref={libraryInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoCapture(e, 'library')} />
            {uploading && <p className="text-[var(--m-text-secondary)] text-xs mt-1 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading...</p>}
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {photos.map((p) => (
                  <div key={p.id || p.url} className="relative">
                    <img src={p.url} alt="POD" className="h-16 w-16 object-cover rounded border border-[var(--m-border-light)]" />
                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((x) => (x.id || x.url) !== (p.id || p.url)))} className="absolute -top-1 -right-1 bg-red-600 rounded-full p-0.5">
                      <X className="h-3 w-3 text-[var(--m-text)]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-[var(--m-text)]" onClick={handleComplete} disabled={saving || uploading} data-testid="complete-stop-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Complete Stop</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteStopDialog;
